// laender.ts — die EINE kurze Länderliste für Adress-Dropdowns.
//
// Vorgeschichte: dieselbe Liste lag zweimal im Code und lief auseinander —
// `SinglePageCompanyForm.tsx` (15 Einträge, u. a. Tschechien, Dänemark,
// Vereinigtes Königreich, China) und `PublicCustomerCheckAddressForm.tsx`
// (11 Einträge, ohne diese vier). Die kürzere war eine echte Teilmenge der
// längeren, deshalb gewinnt die längere; „Sonstige" bleibt letzter Eintrag.
//
// Abgrenzung zur Kontakt-Adressmaske: die Anlage- und Detailmasken eines
// Kontakts (`NewPrivateContactForm`, `NewCompanyContactForm`,
// `ContactDetailsRedesign`) benutzen NICHT eine dieser Kurzlisten, sondern das
// Widget `CountrySelect` über dem vollständigen Länderkatalog
// (`data/nationalities.ts`). Damit Kurzliste und Katalog nicht auseinander
// laufen, prüft `laender.test.ts`, dass jeder Eintrag hier (außer „Sonstige")
// im Katalog unter demselben Namen existiert.
//
// Gespeichert wird durchgehend der deutsche Ländername als Freitext. Bestände
// können deshalb Werte enthalten, die hier nicht stehen — dafür gibt es
// `laenderMitBestandswert()`.

/** Letzter Eintrag jeder Auswahl — bewusst kein Ländername. */
export const LAND_SONSTIGE = "Sonstige";

/** Default-Land einer Anschrift (identisch zu `DEFAULT_COUNTRY`). */
export const LAND_DEFAULT = "Deutschland";

/** Die häufigen Länder für Adress-Dropdowns, „Sonstige" zuletzt. */
export const LAENDER: readonly string[] = [
  "Deutschland",
  "Österreich",
  "Schweiz",
  "Frankreich",
  "Niederlande",
  "Belgien",
  // Luxemburg fehlte als einziges der fünf Nachbarländer, für die es seit
  // 08/2026 eine PLZ-Regel gibt (forms/validation/laenderprofil).
  "Luxemburg",
  "Polen",
  "Tschechien",
  "Dänemark",
  "Italien",
  "Spanien",
  "Vereinigtes Königreich",
  "USA",
  "China",
  LAND_SONSTIGE,
];

/**
 * Bewusste Abweichungen von der Schreibweise des vollständigen Länderkatalogs
 * (`data/nationalities.json`), Kurzlisten-Name → Katalog-Name.
 *
 * „USA" steht seit jeher in beiden alten Kopien und damit auch in den Daten;
 * der Katalog führt das Land als „Vereinigte Staaten". Ein Umbenennen würde
 * jeden gespeicherten Wert zur Ausnahme machen, ohne fachlichen Gewinn — also
 * bleibt „USA" stehen und die Abweichung wird hier dokumentiert statt
 * stillschweigend hingenommen.
 */
export const LAENDER_KATALOG_ABWEICHUNGEN: Record<string, string> = {
  USA: "Vereinigte Staaten",
};

/**
 * Die Auswahlliste inklusive eines gespeicherten Bestandswerts, der nicht in
 * `LAENDER` steht. Ohne das würde ein `<select>` den unbekannten Wert beim
 * Öffnen stillschweigend auf „— wählen —" zurücksetzen und beim nächsten
 * Speichern verwerfen. Der Bestandswert wird vor „Sonstige" einsortiert, damit
 * „Sonstige" letzter Eintrag bleibt.
 */
export function laenderMitBestandswert(value?: string | null): string[] {
  const v = (value ?? "").trim();
  if (!v || LAENDER.includes(v)) return [...LAENDER];
  const ohneSonstige = LAENDER.filter((l) => l !== LAND_SONSTIGE);
  return [...ohneSonstige, v, LAND_SONSTIGE];
}

/**
 * Dieselbe Liste als Auswahl für Masken, die kein eigenes Länder-Widget
 * mitbringen — ohne „Sonstige", weil ein Adressfeld mit dem Wert „Sonstige"
 * nichts aussagt und weder Karte noch PLZ-Regel damit etwas anfangen können.
 */
export const LAENDER_AUSWAHL: readonly string[] = LAENDER.filter((l) => l !== LAND_SONSTIGE);
